import React, {useState} from "react";
import {Alert, Box, Paper, Typography} from "@mui/material";
import {darken, lighten, useTheme} from "@mui/material/styles";
import {Cell, Pie, PieChart, ResponsiveContainer, Tooltip} from "recharts";
import {finiteNumber} from "./distributor-display";
import {pieNameLines, productPieLayout} from "./product-pie-layout";

const labelColor = (color, theme) => theme.palette.mode === "dark" ? lighten(color, 0.25) : darken(color, 0.45);

function ProductCallout({label, row, color, compact, percent}) {
    const theme = useTheme();
    const lines = pieNameLines(row.productName, label.textWidth, compact);
    const direction = label.right ? 1 : -1;
    const endX = label.x - direction * 9;
    return <g aria-label={`${row.productName}: ${percent}`}>
        <title>{row.productName}: {percent}</title>
        <polyline points={`${label.edgeX},${label.edgeY} ${endX - direction * 12},${label.y} ${endX},${label.y}`}
            fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.65} strokeLinejoin="round"/>
        <circle cx={label.edgeX} cy={label.edgeY} r={2.5} fill={color}/>
        <text x={label.x} y={label.y - (lines.length > 1 ? 12 : 4)} textAnchor={label.right ? "start" : "end"}
            fill="currentColor" fontSize={compact ? 11 : 13} fontWeight={500}>
            {lines.map((line, index) => <tspan key={index} x={label.x} dy={index ? 16 : 0}>{line}</tspan>)}
            <tspan x={label.x} dy={21} fill={labelColor(color, theme)} fontSize={compact ? 12 : 14} fontWeight={700}>{percent}</tspan>
        </text>
    </g>;
}

function ProductPieTooltip({active, payload, total, language, t}) {
    const theme = useTheme();
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    const number = new Intl.NumberFormat(language || "en");
    const percent = new Intl.NumberFormat(language || "en", {style: "percent", maximumFractionDigits: 1});
    return <Paper elevation={4} sx={{p: 2, borderRadius: 2, maxWidth: 320, border: 1, borderColor: "divider"}}>
        <Typography variant="body2" fontWeight={600} sx={{mb: 0.5, overflowWrap: "anywhere"}}>{row.productName}</Typography>
        <Typography variant="body2" color="text.secondary">{t("DistributorUnitsSold")}: {number.format(row.totalCount)}</Typography>
        <Typography variant="subtitle2" sx={{color: labelColor(row.fill || theme.palette.primary.main, theme), mt: 0.5}}>{percent.format(row.totalCount / total)}</Typography>
    </Paper>;
}

function LabeledProductPie({rows, colors, language, t}) {
    const [width, setWidth] = useState(960);
    const layout = productPieLayout(rows, width);
    const total = rows.reduce((sum, row) => sum + row.totalCount, 0);
    const percent = new Intl.NumberFormat(language || "en", {style: "percent", maximumFractionDigits: 1});
    const label = ({index}) => <ProductCallout label={layout.labels[index]} row={rows[index]}
        color={colors[index % colors.length]} compact={width < 600} percent={percent.format(layout.labels[index].share)}/>;
    return <Box sx={{color: "text.primary", minWidth: 0}}>
        <ResponsiveContainer width="100%" height={layout.height} onResize={setWidth}>
            <PieChart><Pie data={rows} dataKey="totalCount" nameKey="productName" cx={layout.cx} cy={layout.cy}
                startAngle={90} endAngle={-270} outerRadius={layout.radius} strokeWidth={2} labelLine={false} label={label}
                isAnimationActive={false}>
                {rows.map((row, index) => <Cell key={`${row.productTenantId || index}:${row.currency || "unknown"}`} fill={colors[index % colors.length]}/>)}
            </Pie><Tooltip content={<ProductPieTooltip total={total} language={language} t={t}/>} isAnimationActive={false}
                wrapperStyle={{outline: "none", transition: "none"}}/></PieChart>
        </ResponsiveContainer>
    </Box>;
}

export default function ProductPieChart({data, colors, language, t}) {
    if (data.some(row => !finiteNumber(row.totalCount) || row.totalCount < 0)) {
        return <Alert severity="info">{t("DistributorMetricUnavailable")}</Alert>;
    }
    const rows = data.filter(row => row.totalCount > 0).map(row => ({...row,
        productName: row.productName || t("DistributorMetricUnavailable")}));
    if (!rows.length) return <Typography>{t("DistributorUnitsSold")}: 0</Typography>;
    return <LabeledProductPie rows={rows} colors={colors} language={language} t={t}/>;
}
